import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface SupervisorStats {
  activeAgents: number;
  liveCalls: number;
  inQueue: number;
  avgHandleSeconds: number;
  timelineBreaches: number;
  resolvedToday: number;
}

export interface LiveCallItem {
  channelId: string;
  callerNumber: string;
  agentExtension: string;
  agentName: string;
  durationSeconds: number;
  callStatus: string;
  callStartTime: string;
}

export interface EscalationItem {
  complaintId: number;
  complaintNo: string;
  issue: string;
  citizenName: string;
  phone: string;
  expectedDate: string | null;
  overdueDays: number;
  priority: string;
}

export interface QueueItem {
  queueId: number;
  callerNumber: string;
  channelType: string;
  priority: string;
  topic: string | null;
  queuedAt: string;
  waitSeconds: number;
  queueStatus: string;
}

export interface HourlyItem {
  hour: number;
  count: number;
}

export interface CategoryItem {
  name: string;
  count: number;
  pct: number;
}

export interface AgentStatus {
  userId: number;
  fullName: string;
  sipExtension: string;
  status: 'AVAILABLE' | 'ON_CALL' | 'OFFLINE';
  role?: string;
}

export interface CallbackItem {
  callLogId: number;
  channelId: string;
  callerNumber: string;
  agentExtension: string;
  agentName: string;
  hostSystem: string;
  businessRecordType: string;
  businessRecordId: number;
  disposition: string;
  callReason: string;
  sourceOfInformation: string;
  callbackDueAt: string;
  callbackStatus: 'PENDING' | 'SCHEDULED' | 'COMPLETED' | string;
  callbackResponseSeconds: number;
  overdueSeconds: number;
  wrapUpNotes: string;
}

export interface CallbackSummary {
  open: number;
  overdue: number;
  callbacks: CallbackItem[];
}

export interface OperationalBreakdownItem {
  label: string;
  count: number;
  pct: number;
}

export interface OperationalReport {
  callsToday: number;
  avgHandleSeconds: number;
  avgHoldSeconds: number;
  firstCallResolution: { count: number; pct: number };
  callbacks: { created: number; completed: number; completionPct: number };
  dispositions: OperationalBreakdownItem[];
  callReasons: OperationalBreakdownItem[];
  sources: OperationalBreakdownItem[];
  queues: OperationalBreakdownItem[];
  ivrOptions: OperationalBreakdownItem[];
  hangupParties: OperationalBreakdownItem[];
}

export interface AgentPerformanceItem {
  agentUserId: number;
  agentName: string;
  taskLabel?: string;
  agentExtension: string;
  totalCalls: number;
  handledCalls?: number;
  missedCalls?: number;
  unroutedCalls?: number;
  avgHandleSeconds: number;
  avgHoldSeconds: number;
  firstCallResolutionCount: number;
  firstCallResolutionPct: number;
  callbackCount: number;
  wrapUpCompletionPct: number;
  slaOk?: boolean;
  rowType?: 'AGENT' | 'UNROUTED' | string;
}

export interface AgentPerformanceReport {
  date: string;
  agents: AgentPerformanceItem[];
}

type StompClient = any;

function stompBrokerUrl(url: string): string {
  const w = url.replace(/^http/, 'ws').replace(/\/+$/, '');
  return w.endsWith('/websocket') ? w : `${w}/websocket`;
}

@Injectable({ providedIn: 'root' })
export class SupervisorService {
  private readonly api = environment.apiHost;
  private stomp: StompClient = null;

  readonly stats$       = new BehaviorSubject<SupervisorStats | null>(null);
  readonly liveCalls$   = new BehaviorSubject<LiveCallItem[]>([]);
  readonly escalations$ = new BehaviorSubject<EscalationItem[]>([]);
  readonly queue$       = new BehaviorSubject<QueueItem[]>([]);
  readonly hourly$      = new BehaviorSubject<HourlyItem[]>([]);
  readonly category$    = new BehaviorSubject<CategoryItem[]>([]);
  readonly agents$      = new BehaviorSubject<AgentStatus[]>([]);
  readonly callbacks$   = new BehaviorSubject<CallbackSummary>({ open: 0, overdue: 0, callbacks: [] });
  readonly operationalReport$ = new BehaviorSubject<OperationalReport | null>(null);
  readonly agentPerformance$ = new BehaviorSubject<AgentPerformanceReport | null>(null);

  constructor(private http: HttpClient) {}

  private backgroundOptions(): { headers: HttpHeaders } {
    return { headers: new HttpHeaders({ 'X-Skip-Spinner': 'true' }) };
  }

  fetchStats(): void {
    this.ensureStomp();
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/stats`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? null),
      catchError(() => of(null))
    ).subscribe(d => { if (d) this.stats$.next(d); });
  }

  fetchLiveCalls(): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/calls/live`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    ).subscribe(d => this.liveCalls$.next(d));
  }

  fetchEscalations(): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/escalations`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    ).subscribe(d => this.escalations$.next(d));
  }

  fetchQueue(): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/queue`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    ).subscribe(d => this.queue$.next(d));
  }

  fetchHourly(): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/complaints/hourly`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    ).subscribe(d => this.hourly$.next(d));
  }

  fetchCategory(): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/complaints/by-category`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    ).subscribe(d => this.category$.next(d));
  }

  fetchAgents(): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/calls/agents/availability`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    ).subscribe(d => this.agents$.next(d));
  }

  getAvailableQueueAgents(): Observable<AgentStatus[]> {
    return this.http.get<any>(`${this.api}/api/v1/telephony/queue/available-agents`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? []),
      catchError(() => of([]))
    );
  }

  fetchCallbacks(status = 'ALL'): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/callbacks?status=${encodeURIComponent(status)}`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? { open: 0, overdue: 0, callbacks: [] }),
      catchError(() => of({ open: 0, overdue: 0, callbacks: [] }))
    ).subscribe(d => this.callbacks$.next(d));
  }

  updateCallbackStatus(callLogId: number, callbackStatus: string): void {
    this.http.patch<any>(`${this.api}/api/v1/telephony/supervisor/callbacks/${callLogId}`, { callbackStatus }, this.backgroundOptions()).pipe(
      catchError(() => of(null))
    ).subscribe(() => this.fetchCallbacks());
  }

  fetchOperationalReport(from?: string, to?: string): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/reports/operational${this.dateQuery(from, to)}`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? null),
      catchError(() => of(null))
    ).subscribe(d => this.operationalReport$.next(d));
  }

  fetchAgentPerformance(from?: string, to?: string): void {
    this.http.get<any>(`${this.api}/api/v1/telephony/supervisor/reports/agents${this.dateQuery(from, to)}`, this.backgroundOptions()).pipe(
      map(r => r?.data ?? null),
      catchError(() => of(null))
    ).subscribe(d => this.agentPerformance$.next(d));
  }

  reportExportUrl(kind: 'operational' | 'agents', from?: string, to?: string): string {
    return `${this.api}/api/v1/telephony/supervisor/reports/${kind}/export${this.dateQuery(from, to)}`;
  }

  private dateQuery(from?: string, to?: string): string {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString();
    return query ? `?${query}` : '';
  }

  handleEscalation(id: number): void {
    this.http.patch<any>(`${this.api}/api/v1/telephony/supervisor/escalations/${id}/handle`, {}, this.backgroundOptions()).pipe(
      catchError(() => of(null))
    ).subscribe(() => this.fetchEscalations());
  }

  sendBroadcast(message: string): void {
    this.http.post<any>(`${this.api}/api/v1/telephony/supervisor/broadcast`, { message }, this.backgroundOptions()).pipe(
      catchError(() => of(null))
    ).subscribe();
  }

  assignQueueItem(id: number, assignExt: string): Observable<any> {
    return this.http.patch<any>(
      `${this.api}/api/v1/telephony/queue/${id}/assign`,
      { assignExt },
      this.backgroundOptions()
    ).pipe(
      catchError(err => of({
        error: true,
        message: err?.error?.message || err?.message || 'Unable to assign this queue item'
      }))
    );
  }

  startWhisperCoach(agentExt: string, supervisorExt: string): Observable<any> {
    return this.http.post<any>(
      `${this.api}/api/v1/supervisor/calls/whisper`,
      { agentExt, supervisorExt },
      this.backgroundOptions()
    ).pipe(
      catchError(err => of({
        error: true,
        message: err?.error?.message || err?.message || 'Unable to start coach session'
      }))
    );
  }

  stopWhisperCoach(agentExt: string): void {
    this.http.delete<any>(`${this.api}/api/v1/supervisor/calls/whisper/${encodeURIComponent(agentExt)}`, this.backgroundOptions()).pipe(
      catchError(() => of(null))
    ).subscribe();
  }

  startMonitor(agentExt: string, supervisorExt: string): Observable<any> {
    return this.http.post<any>(
      `${this.api}/api/v1/supervisor/calls/monitor`,
      { agentExt, supervisorExt },
      this.backgroundOptions()
    ).pipe(
      catchError(err => of({
        error: true,
        message: err?.error?.message || err?.message || 'Unable to start monitor session'
      }))
    );
  }

  stopMonitor(agentExt: string): void {
    this.http.delete<any>(`${this.api}/api/v1/supervisor/calls/monitor/${encodeURIComponent(agentExt)}`, this.backgroundOptions()).pipe(
      catchError(() => of(null))
    ).subscribe();
  }

  private ensureStomp(): void {
    if (this.stomp) return;
    this.connectStomp();
  }

  private connectStomp(): void {
    const url = environment.stompUrl;
    if (!url) return;
    import('@stomp/stompjs').then(mod => {
      const Client = mod.Client ?? (mod as any).default?.Client ?? (mod as any).default;
      const client: StompClient = new Client({
        brokerURL: stompBrokerUrl(url),
        reconnectDelay: 5000,
        onConnect: () => {
          client.subscribe('/topic/supervisor/stats', (msg: { body: string }) => {
            try { this.stats$.next(JSON.parse(msg.body)); } catch {}
          });
          client.subscribe('/topic/supervisor/queue', (msg: { body: string }) => {
            try { this.queue$.next(JSON.parse(msg.body)); } catch {}
          });
          client.subscribe('/topic/agents', (msg: { body: string }) => {
            try {
              const updated: AgentStatus = JSON.parse(msg.body);
              const current = this.agents$.value;
              const idx = current.findIndex(a => a.sipExtension === updated.sipExtension);
              if (idx >= 0) {
                const next = [...current]; next[idx] = updated; this.agents$.next(next);
              } else {
                this.agents$.next([...current, updated]);
              }
            } catch {}
          });
        },
      });
      client.activate();
      this.stomp = client;
    });
  }
}
